import React, { useMemo, useState } from "react";
import AuditSymbolCompliance from "./AuditSymbolCompliance.jsx";
import SearchBar from "./SearchBar";
import ProfileModal from "./modals/ProfileModal.jsx";
import { FaTrash } from "react-icons/fa";
import UninstallProfileModal from "./modals/UninstallProfileModal.jsx";
import { disableProfile, enableProfile, reapplyProfile } from "../commands/mdmCommands.js";

function sortProfiles(Profiles) {
  let appArray = [...Profiles];
  return appArray.sort((a, b) => {
    const left = String(a.PayloadDisplayName || a.PayloadIdentifier || "").toLowerCase();
    const right = String(b.PayloadDisplayName || b.PayloadIdentifier || "").toLowerCase();
    return left > right ? 1 : -1;
  });
}

function statusBadgeClass(status) {
  if (status === "Aktiv") return "text-bg-success";
  if (status === "Deaktiviert") return "text-bg-warning";
  if (status === "Zugewiesen") return "text-bg-info";
  if (status === "Hintergrund") return "text-bg-secondary";
  return "text-bg-light";
}

export default function ProfilesTable({
  Profiles,
  assignedSettingsProfiles,
  disabledProfiles,
  Administrator,
  UDID,
  onDataChanged,
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [selectedProfileDisplayName, setSelectedProfileDisplayName] =
    useState(null);
  const [selectedProfileIdentifier, setSelectedProfileIdentifier] =
    useState(null);
  const [showUninstallProfileModal, setShowUninstallModalProfile] =
    useState(false);

  const disabledIdentifierSet = useMemo(
    () =>
      new Set(
        (disabledProfiles || [])
          .map((p) => p?.PayloadIdentifier)
          .filter(Boolean)
          .map((identifier) => identifier.toLowerCase())
      ),
    [disabledProfiles]
  );

  const allProfiles = useMemo(() => {
    const installedList = Array.isArray(Profiles) ? Profiles : [];
    const profileIdByIdentifier = new Map(
      (assignedSettingsProfiles || [])
        .filter((p) => p?.profileId && p?.PayloadIdentifier)
        .map((p) => [String(p.PayloadIdentifier).toLowerCase(), p.profileId])
    );
    const rows = installedList.map((profile) => {
      const identifier = String(profile?.PayloadIdentifier || "").toLowerCase();
      return {
        ...profile,
        __profileId: profileIdByIdentifier.get(identifier) || null,
        __status: disabledIdentifierSet.has(identifier)
          ? "Deaktiviert"
          : "Aktiv",
        __isPlaceholder: false,
      };
    });

    const installedIdentifiers = new Set(
      installedList
        .map((p) => p?.PayloadIdentifier)
        .filter(Boolean)
        .map((identifier) => identifier.toLowerCase())
    );

    const assignedIdentifiers = new Set(
      (assignedSettingsProfiles || [])
        .map((p) => p?.PayloadIdentifier)
        .filter(Boolean)
        .map((identifier) => identifier.toLowerCase())
    );

    for (const disabledProfile of disabledProfiles || []) {
      const identifier = disabledProfile?.PayloadIdentifier;
      if (!identifier) continue;
      if (installedIdentifiers.has(identifier.toLowerCase())) continue;
      if (assignedIdentifiers.has(identifier.toLowerCase())) continue;
      rows.push({
        PayloadDisplayName: disabledProfile.PayloadDisplayName || identifier,
        PayloadIdentifier: identifier,
        PayloadVersion: "-",
        IsEncrypted: null,
        PayloadRemovalDisallowed: null,
        PayloadContent: [],
        __status: "Deaktiviert",
        __isPlaceholder: true,
      });
    }

    for (const assigned of assignedSettingsProfiles || []) {
      const identifier = assigned?.PayloadIdentifier;
      if (!identifier) continue;
      const key = identifier.toLowerCase();
      if (installedIdentifiers.has(key)) continue;
      if (rows.some((row) => String(row.PayloadIdentifier || "").toLowerCase() === key)) continue;
      const disabled = disabledIdentifierSet.has(key);
      const isSettings = assigned.kind === "settings" || assigned.kind === "wallpaper";
      rows.push({
        PayloadDisplayName: assigned.PayloadDisplayName || identifier,
        PayloadIdentifier: identifier,
        PayloadUUID: assigned.PayloadUUID,
        PayloadDescription: assigned.PayloadDescription,
        PayloadType: assigned.PayloadType,
        PayloadVersion: "-",
        IsEncrypted: null,
        PayloadRemovalDisallowed: null,
        PayloadContent: [],
        __profileId: assigned.profileId || null,
        __status: disabled ? "Deaktiviert" : (isSettings ? "Hintergrund" : "Zugewiesen"),
        __isPlaceholder: true,
        __isSettingsProfile: isSettings,
        __isAssignedConfig: !isSettings,
        __kind: assigned.kind || "config",
        __groupName: assigned.groupName || "",
      });
    }

    return rows;
  }, [Profiles, assignedSettingsProfiles, disabledProfiles, disabledIdentifierSet]);

  const searchedProfiles = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return allProfiles;
    return allProfiles.filter((profile) =>
      [profile.PayloadDisplayName, profile.PayloadIdentifier, profile.__groupName]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(query)
    );
  }, [allProfiles, searchQuery]);

  function searchHandler(query) {
    setSearchQuery(query);
  }

  function showProfileDetails(profile) {
    setSelectedProfile(profile);
    setIsModalVisible(true);
  }

  function hideProfileDetails() {
    setIsModalVisible(false);
  }

  function displayUninstallProfileModal(name, ident) {
    setSelectedProfileDisplayName(name);
    setSelectedProfileIdentifier(ident);
    setShowUninstallModalProfile(true);
  }

  function hideUninstallProfileModal() {
    setShowUninstallModalProfile(false);
  }

  function deactivateProfile(profile) {
    const identifier = profile?.PayloadIdentifier;
    if (!identifier) return;
    const profileName = profile?.PayloadDisplayName || identifier;
    disableProfile(UDID, identifier, profileName);
    if (onDataChanged) onDataChanged();
  }

  function activateProfile(profile) {
    const identifier = profile?.PayloadIdentifier;
    if (!identifier) return;
    const profileName = profile?.PayloadDisplayName || identifier;
    enableProfile(UDID, identifier, profileName);
    if (onDataChanged) onDataChanged();
  }

  function reapplySettingsProfile(profile) {
    const identifier = profile?.PayloadIdentifier;
    if (!identifier) return;
    const profileName = profile?.PayloadDisplayName || identifier;
    reapplyProfile(UDID, identifier, profileName);
    if (onDataChanged) onDataChanged();
  }

  return (
    <div>
    <div className="table-responsive" style={{ maxHeight: "70vh", overflow: "auto" }}>
      <table className='table tableList'>
        <thead>
          <tr>
            <th>
              <SearchBar searchHandler={searchHandler} />
            </th>
            <th style={{ minWidth: 120 }}>Identifier</th>
            <th>Version</th>
            <th>Status</th>
            <th>Encrypted</th>
            <th>User Removable</th>
            {Administrator ? <th>Aktion</th> : null}
          </tr>
        </thead>
        <tbody>
          {sortProfiles(searchedProfiles).map((profile, index) => {
            const identifier = String(profile.PayloadIdentifier || "");
            const shortId = identifier.length > 48
              ? `${identifier.slice(0, 22)}…${identifier.slice(-18)}`
              : identifier;
            return (
              <tr key={(profile.PayloadIdentifier || profile.PayloadDisplayName) + index}>
                <td onClick={() => showProfileDetails(profile)}>
                  {profile.PayloadDisplayName}
                  {profile.__groupName ? (
                    <div className="small text-muted">Gruppe: {profile.__groupName}</div>
                  ) : null}
                </td>
                <td
                  onClick={() => showProfileDetails(profile)}
                  title={identifier}
                  className="small text-muted"
                  style={{ maxWidth: 220, wordBreak: "break-all", overflowWrap: "anywhere" }}
                >
                  {shortId}
                </td>
                <td onClick={() => showProfileDetails(profile)}>
                  {profile.PayloadVersion}
                </td>
                <td>
                  <span className={`badge ${statusBadgeClass(profile.__status)}`}>
                    {profile.__status}
                  </span>
                </td>
                <td onClick={() => showProfileDetails(profile)}>
                  {profile.IsEncrypted === null ? "—" : <AuditSymbolCompliance status={profile.IsEncrypted} />}
                </td>
                <td onClick={() => showProfileDetails(profile)}>
                  {profile.PayloadRemovalDisallowed === null ? (
                    "—"
                  ) : (
                    <AuditSymbolCompliance
                      status={!profile.PayloadRemovalDisallowed}
                    />
                  )}
                </td>
                {Administrator ? (
                  <td>
                    {profile.__status === "Deaktiviert" ? (
                      <button
                        type='button'
                        className='btn btn-sm btn-outline-success'
                        onClick={() => activateProfile(profile)}
                      >
                        Aktivieren
                      </button>
                    ) : profile.__isSettingsProfile ? (
                      <div className='d-flex gap-2 align-items-center'>
                        <button
                          type='button'
                          className='btn btn-sm btn-outline-primary'
                          onClick={() => reapplySettingsProfile(profile)}
                        >
                          Erneut setzen
                        </button>
                        <button
                          type='button'
                          className='btn btn-sm btn-outline-warning'
                          onClick={() => deactivateProfile(profile)}
                        >
                          Deaktivieren
                        </button>
                      </div>
                    ) : (
                      <div className='d-flex gap-2 align-items-center flex-wrap'>
                        {profile.__isAssignedConfig && profile.__isPlaceholder ? (
                          <button
                            type='button'
                            className='btn btn-sm btn-outline-primary'
                            onClick={() => activateProfile(profile)}
                            title="InstallProfile anstoßen"
                          >
                            Installieren
                          </button>
                        ) : null}
                        <button
                          type='button'
                          className='btn btn-sm btn-outline-warning'
                          onClick={() => deactivateProfile(profile)}
                        >
                          Deaktivieren
                        </button>
                        {!profile.__isPlaceholder ? (
                          <FaTrash
                            onClick={() =>
                              displayUninstallProfileModal(
                                profile.PayloadDisplayName,
                                profile.PayloadIdentifier
                              )
                            }
                          />
                        ) : null}
                      </div>
                    )}
                  </td>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
      <ProfileModal
        visible={isModalVisible}
        profile={selectedProfile}
        hideProfileDetails={hideProfileDetails}
      />
      <UninstallProfileModal
        visible={showUninstallProfileModal}
        UDID={UDID}
        displayName={selectedProfileDisplayName}
        identifier={selectedProfileIdentifier}
        hideUninstallProfileModal={hideUninstallProfileModal}
      />
    </div>
  );
}
